import { Fragment, useEffect, useRef, useState } from 'react';
import { getPickListPdf } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { qty } from '@/lib/format';
import { useSession } from '@/stores/session';
import type { ReadyOrder } from './types';
import s from './PickListDialog.module.css';

export function PickListDialog({ orders, onClose }: { orders: ReadyOrder[]; onClose: () => void }) {
  const ff = useFormFactor();
  const canAmount = useSession((state) => state.can('orders.amount.read'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [showProductImages, setShowProductImages] = useState(ff !== 'tablet');
  const [showAmounts, setShowAmounts] = useState(false);
  const [tabletPrintOptionsOpen, setTabletPrintOptionsOpen] = useState(true);
  const [tabletCopies, setTabletCopies] = useState(1);
  const [tabletDuplex, setTabletDuplex] = useState(false);
  const [tabletMonochrome, setTabletMonochrome] = useState(true);
  const [previewPage, setPreviewPage] = useState(1);
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tabletAutoStarted = useRef(false);
  const customerNames = [...new Set(orders.map((order) => order.customer.name))];
  const scope = `已选 ${orders.length} 单${customerNames.length === 1 ? ` · ${customerNames[0]}` : ''}`;
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const generate = async (overrides: { showProductImages?: boolean; showAmounts?: boolean } = {}) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await getPickListPdf(orders.map((order) => order.id), {
        showProductImages: overrides.showProductImages ?? showProductImages,
        showAmounts: overrides.showAmounts ?? showAmounts,
      });
      const { blob } = result;
      setPageCount(result.pageCount);
      setPdfUrl(URL.createObjectURL(blob));
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const part = (name: string) => parts.find((item) => item.type === name)?.value || '';
      setPdfFile(new File([blob], `拣货单-${part('year')}-${part('month')}-${part('day')}.pdf`, { type: 'application/pdf' }));
    } catch (cause) { setError(cause instanceof Error ? cause.message : '拣货单生成失败'); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (ff !== 'tablet' || tabletAutoStarted.current) return;
    tabletAutoStarted.current = true;
    void generate();
  }, [ff]);
  useEffect(() => { setPreviewPage(1); }, [pdfFile]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (ff !== 'tablet' || !pdfFile || !canvas) return;
    let cancelled = false;
    let documentTask: import('pdfjs-dist').PDFDocumentLoadingTask | undefined;
    let renderTask: { cancel: () => void; promise: Promise<void> } | undefined;
    let resizeObserver: ResizeObserver | undefined;
    setPdfPreviewError('');
    const render = async () => {
      try {
        const [{ getDocument, GlobalWorkerOptions }, worker] = await Promise.all([
          import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]);
        if (cancelled) return;
        GlobalWorkerOptions.workerSrc = worker.default;
        const data = new Uint8Array(await pdfFile.arrayBuffer());
        if (cancelled) return;
        documentTask = getDocument({ data });
        const pdf = await documentTask.promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        const surface = canvas.parentElement;
        if (!surface) return;
        let lastWidth = 0;
        const drawPage = async () => {
          const bounds = surface.getBoundingClientRect();
          if (cancelled || bounds.width < 200 || Math.abs(bounds.width - lastWidth) < 2 || !canvasRef.current) return;
          lastWidth = bounds.width;
          renderTask?.cancel();
          const page = await pdf.getPage(Math.min(previewPage, pdf.numPages));
          if (cancelled || !canvasRef.current) return;
          const base = page.getViewport({ scale: 1 });
          const ratio = window.devicePixelRatio || 1;
          const scale = bounds.width / base.width;
          const viewport = page.getViewport({ scale: scale * ratio });
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.style.width = `${Math.ceil(viewport.width / ratio)}px`;
          canvas.style.height = `${Math.ceil(viewport.height / ratio)}px`;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('无法创建 PDF 预览画布');
          const task = page.render({ canvas, canvasContext: context, viewport });
          renderTask = task;
          try { await task.promise; }
          catch (cause) { if ((cause as Error).name !== 'RenderingCancelledException') throw cause; }
        };
        resizeObserver = new ResizeObserver(() => { void drawPage().catch((cause) => { if (!cancelled) setPdfPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败'); }); });
        resizeObserver.observe(surface);
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await drawPage();
      } catch (cause) {
        if (!cancelled) setPdfPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败');
      }
    };
    void render();
    return () => { cancelled = true; resizeObserver?.disconnect(); renderTask?.cancel(); void documentTask?.destroy(); };
  }, [ff, pdfFile, previewPage]);
  const share = async () => {
    if (!pdfFile || !navigator.share || !navigator.canShare?.({ files: [pdfFile] })) return;
    try { await navigator.share({ title: '拣货单', files: [pdfFile] }); }
    catch (cause) { if (!(cause instanceof DOMException && cause.name === 'AbortError')) setError(cause instanceof Error ? cause.message : '分享失败'); }
  };
  const canShare = !!pdfFile && !!navigator.share && !!navigator.canShare?.({ files: [pdfFile] });
  const phoneOptions = ff === 'phone' && !pdfUrl;
  const phonePreview = ff === 'phone' && !!pdfUrl;
  const tabletOptions = ff === 'tablet' && !pdfUrl;
  const tabletPreview = ff === 'tablet';
  const pageLabel = pageCount ? `共 ${pageCount} 页` : pdfUrl ? '正在读取页数…' : '正在生成 PDF…';
  const tabletDate = new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', weekday: 'short', timeZone: 'Asia/Shanghai' }).format(new Date());
  return <div className={s.scrim} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={`${s.dialog} ${pdfUrl ? s.wide : ''} ${tabletOptions && !tabletPreview ? s.tabletDialog : ''} ${tabletPreview ? s.tabletPreviewDialog : ''}`} role="dialog" aria-modal="true" aria-label={tabletPreview || pdfUrl ? '拣货单 PDF 预览' : '打印'}>
      {tabletPreview ? <div className={s.tabletPreviewFrame}>
        <div className={s.tabletStatusBar}><span>9:41　{tabletDate}</span><span>●●●　Wi-Fi　100%</span></div>
        <header className={s.tabletPreviewHeader}>
          <button type="button" className={s.tabletDone} onClick={onClose}>完成</button>
          <div className={s.tabletTitle}><strong>拣货单</strong><small>{scope} · A4 纵向 · {pageLabel}</small></div>
          <div className={s.tabletHeaderActions}>
            <button type="button" className={s.tabletShare} onClick={() => void share()} disabled={!canShare} aria-label="分享拣货单 PDF" title={canShare ? '分享拣货单 PDF' : '当前浏览器不支持系统分享'}><Icon name="share" size={20} /></button>
            <button type="button" className={s.tabletPrint} onClick={() => setTabletPrintOptionsOpen((open) => !open)} disabled={!pdfUrl}><Icon name="print" size={18} />打印</button>
          </div>
        </header>
        <div className={s.tabletPreviewBody}>
          <aside className={s.tabletPrintSidebar}>
            <section><h3>打印内容</h3><div className={s.tabletPrintTypes} role="group" aria-label="打印内容">
              <button type="button" className={s.tabletPrintTypeActive}><Icon name="list" size={18} />拣货单</button>
              <button type="button" disabled title="所选订单尚未形成发货批次">发货单</button>
              <button type="button" disabled title="所选订单尚无物流单号与面单数据">物流面单</button>
            </div></section>
            <section className={s.tabletPrintRange}><h3>范围</h3><p>已选 {orders.length} 单 · {pageLabel}</p></section>
            <section><h3>选项</h3><div className={s.tabletPreviewOptions}>
              <label><span><b>套装展开成组件</b><small>拣货时按商品备货</small></span><input type="checkbox" checked disabled readOnly aria-label="套装展开成组件" /></label>
              <label><span><b>显示产品图片</b></span><input type="checkbox" checked={showProductImages} onChange={(event) => { const next = event.target.checked; setShowProductImages(next); void generate({ showProductImages: next }); }} aria-label="显示产品图片" /></label>
              <label className={s.tabletAmountOption}><span><b>显示单价和金额</b><small>拣货单不印金额</small></span><input type="checkbox" checked={false} disabled readOnly aria-label="显示单价和金额，拣货单不印金额" /></label>
            </div></section>
            <section className={s.tabletPaperSetting}><h3>纸张</h3><p>A4 · 纵向</p></section>
            {error && <p className={s.error} role="alert">{error} <button type="button" onClick={() => void generate()}>重试</button></p>}
          </aside>
          <main className={s.tabletPaperArea}>
            <div className={s.tabletPaperStack}>
              {pdfPreviewError ? <p className={s.error} role="alert">{pdfPreviewError}</p> : <div className={s.tabletPdfPageSurface}><canvas ref={canvasRef} aria-label={`拣货单 PDF 第 ${previewPage} 页`} />{(busy || !pdfUrl) && <span className={s.tabletPdfLoading}>正在生成 PDF…</span>}</div>}
            </div>
            <div className={s.tabletPageIndicator}><button type="button" aria-label="上一页" onClick={() => setPreviewPage((page) => Math.max(1, page - 1))} disabled={!pdfUrl || previewPage <= 1}>‹</button><span>{previewPage} / {pageCount || '…'}</span><button type="button" aria-label="下一页" onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))} disabled={!pdfUrl || previewPage >= pageCount}>›</button></div>
          </main>
          {tabletPrintOptionsOpen && <section className={s.airPrintPopover} aria-label="打印机选项">
            <h3>打印机选项</h3>
            <div className={s.airPrintRow}><span><Icon name="print" size={16} />打印机</span><b>选择系统打印机　›</b></div>
            <div className={s.airPrintRow}><span>份数</span><div className={s.copiesControl}><button type="button" aria-label="减少份数" onClick={() => setTabletCopies((count) => Math.max(1, count - 1))}>−</button><b>{tabletCopies}</b><button type="button" aria-label="增加份数" onClick={() => setTabletCopies((count) => count + 1)}>＋</button></div></div>
            <div className={s.airPrintRow}><span>范围</span><b>{pageCount ? `全部 ${pageCount} 页` : '读取页数中…'}</b></div>
            <button type="button" aria-pressed={tabletDuplex} className={`${s.airPrintToggle} ${tabletDuplex ? s.airPrintToggleOn : ''}`} onClick={() => setTabletDuplex((value) => !value)}>双面 <span /></button>
            <button type="button" aria-pressed={tabletMonochrome} className={`${s.airPrintToggle} ${tabletMonochrome ? s.airPrintToggleOn : ''}`} onClick={() => setTabletMonochrome((value) => !value)}>黑白 <span /></button>
            <button type="button" className={s.airPrintSubmit} disabled={!pdfUrl} onClick={() => { if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer'); }}>打印 {pageCount || '…'} 页</button>
          </section>}
        </div>
      </div> : <>
      <header className={`${s.controls} ${phoneOptions ? s.phoneControls : ''} ${phonePreview ? s.phonePreviewControls : ''} ${tabletOptions ? s.tabletControls : ''}`}>
        {phoneOptions && <span className={s.handle} aria-hidden="true" />}
        {phoneOptions && <button type="button" className={`${s.close} ${s.phoneClose}`} aria-label="关闭" onClick={onClose} disabled={busy}>×</button>}
        <strong>{pdfUrl ? '拣货单 · 预览' : '打印'}</strong>
        {phoneOptions && <small className={s.scope}>{scope}</small>}
        {tabletOptions && <small className={s.tabletScope}>{scope}</small>}
        {phonePreview ? <button type="button" className={s.phoneDone} onClick={onClose} disabled={busy}>完成</button> : !phoneOptions && <button type="button" className={s.close} aria-label={pdfUrl ? '返回选项' : '关闭'} onClick={pdfUrl ? () => setPdfUrl('') : onClose} disabled={busy}>{pdfUrl ? '返回选项' : '×'}</button>}
        {pdfUrl && (phonePreview ? <button type="button" onClick={() => void share()} disabled={!canShare} title={canShare ? '分享拣货单 PDF' : '当前浏览器不支持系统分享'}>分享</button> : canShare && <button type="button" onClick={() => void share()}>分享 PDF</button>)}
        {pdfUrl && !phonePreview && <a className={s.action} href={pdfUrl} download={pdfFile?.name || '拣货单.pdf'}>下载 PDF</a>}
        {pdfUrl && !phonePreview && <a className={`${s.action} ${s.primary}`} href={pdfUrl} target="_blank" rel="noopener noreferrer">打开 PDF 打印</a>}
      </header>
      {error && <p className={s.error} role="alert">{error}</p>}
      {pdfUrl ? <div className={s.preview}><p className={`${s.previewNote} ${phonePreview ? s.phonePreviewNote : ''}`}>下方为选单时的内容预览。正式数量以刚生成的 PDF 为准；打印请打开 PDF。</p>
        {phonePreview && <div className={s.phonePreviewBadges}><span>按货位排序</span><span>套装已展开</span><span>不印金额</span></div>}
        {orders.map((order) => <article className={s.sheet} key={order.id}>
          <h1>拣货单</h1>
          <div className={s.meta}><span>业务订单：{order.orderNo}</span><span>客户：{order.customer.name}</span><span>仓库：{order.warehouse.name}</span></div>
          <p className={s.locationNote}>仓库未提供货位数据，货位列留空；请按实物复核。</p>
          <div className={s.tableWrap}><table className={s.table}><thead><tr><th>核</th><th>货位</th><th>货物</th><th>编号</th><th>数量</th><th>备注</th></tr></thead><tbody>
            {order.readyGroups.filter((group) => group.quantity > 0).map((group, index) => <Fragment key={`${group.label}-${index}`}>
              {group.unit === '套' && <tr className={s.bundle}><td colSpan={6}>{group.label} × {qty(group.quantity)} 套 · 组件明细</td></tr>}
              {group.lines.filter((line) => line.quantity > 0).map((line, lineIndex) => <tr key={`${line.productCode}-${lineIndex}`}><td><span className={s.checkBox} /></td><td>—</td><td>{line.productName}</td><td>{line.productCode}</td><td>{qty(line.quantity)}</td><td>{group.unit === '套' ? '套装组件' : ''}</td></tr>)}
            </Fragment>)}
          </tbody></table></div>
          <footer>拣货人：________________　复核人：________________</footer>
          <p className={s.footnote}>本单为打印时的库存快照。正式出库仍会再次校验订单状态和库存。</p>
        </article>)}
      </div> : <>
        <div className={s.options}>
          <h3>打印内容 <span>*</span></h3>
          <div className={`${s.choices} ${tabletOptions ? s.tabletChoices : ''}`} role="group" aria-label="打印内容">
            <button type="button" className={s.selected} aria-pressed="true"><Icon name="list" size={20} />拣货单</button>
            <button type="button" disabled title="所选订单尚未形成发货批次；出库后在批次详情生成发货单"><Icon name="file" size={20} />发货单</button>
            <button type="button" disabled title="所选订单尚无物流单号与面单数据"><Icon name="barcode" size={20} />物流面单</button>
          </div>
          <p className={s.help}>拣货单按仓位排序，方便一次走完；发货单给客户签收。</p>
          <div className={`${s.settings} ${tabletOptions ? s.tabletSettings : ''}`}>
            <div><h3>范围</h3><p className={s.value}>{scope}{!phoneOptions && <span aria-hidden="true">›</span>}</p></div>
            <div><h3>纸张</h3><p className={s.value}>A4 · 纵向<span aria-hidden="true">›</span></p></div>
          </div>
          <h3 className={`${s.optionHeading} ${tabletOptions ? s.tabletOptionHeading : ''}`}>选项</h3>
          <div className={`${s.optionList} ${tabletOptions ? s.tabletOptionList : ''}`}>
            <div className={s.option}><span><b>套装展开成组件明细</b>{(phoneOptions || tabletOptions) && <small>不展开拣货员不知道拿什么</small>}</span><input type="checkbox" checked disabled readOnly aria-label="套装展开成组件明细" /></div>
            <label className={s.option}><span><b>显示产品图片</b></span><input type="checkbox" checked={showProductImages} onChange={(event) => setShowProductImages(event.target.checked)} aria-label="显示产品图片" /></label>
            {canAmount && <label className={s.option}><span><b>显示单价和金额</b>{(phoneOptions || tabletOptions) && <small>仓库拣货一般不需要金额，默认关闭</small>}</span><input type="checkbox" checked={showAmounts} onChange={(event) => setShowAmounts(event.target.checked)} aria-label="显示单价和金额" /></label>}
          </div>
          {!phoneOptions && !tabletOptions && <p className={s.optionNote}>仓库拣货一般不需要金额，默认关闭</p>}
        </div>
        <footer className={s.footer}>
          <button type="button" onClick={onClose} disabled={busy}>取消</button>
          <button type="button" className={s.primary} onClick={() => void generate()} disabled={busy || !orders.length}>
            {busy ? '正在生成 PDF…' : ff === 'phone' ? <><Icon name="file" />生成 PDF 预览</> : '打印'}
          </button>
        </footer>
      </>}
      {phonePreview && <footer className={s.phonePreviewFooter}><span className={s.phonePageCount}>共 {pageCount || orders.length} 页 · 双指放大</span><a href={pdfUrl} target="_blank" rel="noopener noreferrer">打印</a></footer>}
      </>}
    </section>
  </div>;
}
